import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, User, AuthError, getAdditionalUserInfo } from 'firebase/auth';
import { doc, setDoc, getFirestore, getDoc, serverTimestamp } from 'firebase/firestore';
import { checkEmailExists } from '../services/userService';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Initialize Firestore
  const db = getFirestore();
  
  // Function to create or update user in Firestore
  const createUserInFirestore = async (user: User) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      
      // Check if the user document already exists
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // Check if this email is already associated with another account
        if (user.email) {
          const emailCheck = await checkEmailExists(user.email);
          
          if (emailCheck.exists && emailCheck.userId !== user.uid) {
            console.warn(`Email ${user.email} already exists with different user ID: ${emailCheck.userId}`);
            // We'll handle this in signInWithGoogle, just create the user normally for now
          }
        }
        
        // If the user doesn't exist, create a new document
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp()
        });
        console.log('New user created in Firestore');
        return true; // New user
      } else {
        // If the user exists, just update the lastLogin
        await setDoc(userRef, {
          lastLogin: serverTimestamp(),
          // Ensure email is up to date (in case it changed in auth but not Firestore)
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        }, { merge: true });
        console.log('Updated existing user in Firestore');
        return false; // Existing user
      }
    } catch (error) {
      console.error('Error creating/updating user in Firestore:', error);
      return false;
    }
  };
  
  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setAuthError(null);
      
      if (currentUser) {
        // Create or update the user in Firestore and check if new
        const isFirstTimeUser = await createUserInFirestore(currentUser);
        
        // Check if this is a new user (first sign in)
        const isFirstLogin = !localStorage.getItem('hasCompletedFirstLogin');
        setIsNewUser(isFirstLogin);
        
        if (isFirstLogin) {
          // Mark that user has logged in before
          localStorage.setItem('hasCompletedFirstLogin', 'true');
        }
      }
      
      // Update user state
      setUser(currentUser);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Google sign in with duplicate email check
  const signInWithGoogle = async () => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const additionalInfo = getAdditionalUserInfo(result);
      
      // Check if this is a first-time sign-in
      if (additionalInfo?.isNewUser && user.email) {
        // Check if email already exists with a different account
        const emailCheck = await checkEmailExists(user.email);
        
        if (emailCheck.exists && emailCheck.userId !== user.uid) {
          console.warn(`New sign-in with email ${user.email} but this email already exists with user ID: ${emailCheck.userId}`);
          
          // Handle the duplicate account situation:
          // Option 1: Alert the user and continue
          console.log("This email is already associated with an existing account. Data will be kept separate.");
          
          /* 
          // Option 2: If you want to merge accounts in the future, you could add code like:
          // This would require more complex logic to merge user data
          // await mergeUserAccounts(emailCheck.userId, user.uid);
          */
        }
      }
      
      return true;
    } catch (error) {
      console.error("Error signing in with Google:", error);
      const authError = error as AuthError;
      setAuthError(authError.message || "Failed to sign in with Google");
      return false;
    } finally {
      setAuthLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await auth.signOut();
      return true;
    } catch (error) {
      console.error("Error signing out:", error);
      return false;
    }
  };
  
  return {
    user,
    setUser,
    isNewUser,
    setIsNewUser,
    authLoading,
    authError,
    signInWithGoogle,
    signOut
  };
}; 